// BUG_REPORTER — TEMPORARY FEATURE. Delete this folder per docs/BUG_REPORTER.md.

import { useCallback, useState } from 'react';

export type Capture = {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
  /**
   * Where the reporter's own viewport began, as a fraction of the image on each
   * axis. The annotation view scrolls there, so a full-page capture opens on the
   * rows AND the columns they were looking at rather than at the top-left corner
   * of a 9000px image.
   */
  focus: { x: number; y: number };
  /**
   * How many of the reporter's viewport widths the image spans — 1 unless the
   * page had columns hidden off to the right. The annotation pane multiplies its
   * own width by this and scrolls, instead of squeezing a 2,600px capture into a
   * 600px pane and making every label in it unreadable.
   */
  viewportsWide: number;
};
export type CaptureResult = { capture: Capture | null; error: string | null };

// html2canvas walks and clones the whole document. On a data-dense page
// (VendorInvoicesPage) that is not instant, and there is no upper bound on how
// long a pathological layout can take. The form must open either way, so the
// capture races a timer and loses gracefully.
//
// Still 8s now that the captures are taller, and that is measured rather than
// hopeful: in headless Chromium a 1600x5560 capture of this kind of grid took
// ~0.5s and a 1600x17560 one ~1.1s. The budget below (16MP) sits between them,
// so a client machine several times slower still stays inside the clock.
//
// What actually spends that clock is the ELEMENT COUNT, not the pixel count —
// html2canvas clones every node and copies ~340 computed properties onto each.
// Measured in headless Chromium on a bordered grid: ~1,900 cells took 5.9s and
// ~2,900 took past 8s and timed out. That ceiling is the library's and predates
// the columns being captured at all (the same grid, captured at the viewport's
// width by the code before 2026-09-09, timed out at 9.3s where this takes 8.7s).
// Widening the region costs about 10% on a grid that dense. If this becomes the
// common failure, the fix is fewer nodes in the clone, not a longer wait.
const CAPTURE_TIMEOUT_MS = 8000;

// The capture is taken at devicePixelRatio so text stays readable, but a 3x
// phone screen would produce a needlessly enormous PNG for no extra legibility.
// Only the FALLBACK renderer honours this — see render() for why the
// foreignObject path is pinned to 1.
const MAX_SCALE = 2;
// Below 1 the app's 12px labels stop being readable, so this is the floor:
// past it we shrink the region, not the resolution.
const MIN_SCALE = 1;

/**
 * The pixel budget for one capture. Three numbers had to fit under it:
 *
 *  - Browsers cap canvas AREA, and the tightest common ceiling is Safari's
 *    16,777,216 (4096²). 16MP keeps us inside it everywhere. Measured past it:
 *    at 130MP Chromium still produced a canvas but toBlob() returned null —
 *    i.e. the report would have gone up with no image at all.
 *  - BUG_REPORT_MAX_IMAGE_BYTES is 8MB and an oversize upload 413s the WHOLE
 *    submission, not just the picture (bugReport.service.js). A text-dense
 *    capture measured 0.11 bytes/pixel, so 16MP is ~1.8MB — still under 8MB
 *    at triple that density.
 *  - It has to be enough for the screen this feature exists for. At a 1920px
 *    viewport 16MP is 8,333px of height, ~230 rows of a 36px invoice grid; at
 *    1366px wide it is 11,700px. The 200-row grid fits whole.
 */
const MAX_CAPTURE_PIXELS = 16_000_000;
// Chrome and Safari both refuse a canvas dimension past this.
const MAX_CAPTURE_SIDE = 16_384;

/** Anything the reporter scrolls INSIDE the page, rather than with the window. */
const SCROLLABLE = /(auto|scroll|overlay)/;

class CaptureTimeout extends Error {}

function timeout(ms: number): Promise<never> {
  return new Promise((_, reject) =>
    setTimeout(() => reject(new CaptureTimeout('The screen took too long to capture.')), ms)
  );
}

/** One scroll container, with how much it is hiding on each axis. */
type Scroller = { el: HTMLElement; hiddenX: number; hiddenY: number };

/**
 * Every element with content scrolled out of sight inside it, on EITHER axis.
 *
 * This is the whole bug. The screen that produces most reports is a fullScreen
 * <Modal> whose body is `flex-1 min-h-0 overflow-y-auto`: the grid scrolls in
 * there, `window.scrollY` never leaves 0, and the rows the reporter is pointing
 * at are 3000px inside a container nobody was measuring.
 *
 * The horizontal axis is the same story told sideways, and was still untold
 * until 2026-09-09: the quotation grid and the client invoice are wide tables
 * inside an `overflow-x-auto` wrapper, so the columns on the right — margin,
 * line total, the ones a pricing bug actually shows up in — sit outside the
 * container's client box exactly as those rows did.
 */
function scrollContainers(doc: Document): Scroller[] {
  const view = doc.defaultView;
  if (!view) return [];
  const found: Scroller[] = [];
  Array.from(doc.querySelectorAll<HTMLElement>('*')).forEach((el) => {
    const style = view.getComputedStyle(el);
    // A 1px tolerance on each axis: a fractional layout width rounds up into
    // scrollWidth on boxes that are not scrollable in any way a person can see.
    const hiddenX =
      el.scrollWidth > el.clientWidth + 1 && SCROLLABLE.test(style.overflowX) ? el.scrollWidth - el.clientWidth : 0;
    const hiddenY =
      el.scrollHeight > el.clientHeight + 1 && SCROLLABLE.test(style.overflowY) ? el.scrollHeight - el.clientHeight : 0;
    if (hiddenX || hiddenY) found.push({ el, hiddenX, hiddenY });
  });
  return found;
}

export type CaptureGeometry = {
  viewportWidth: number;
  viewportHeight: number;
  /** documentElement.scroll{Width,Height} — the window-scrolled part of the page. */
  documentWidth: number;
  documentHeight: number;
  /** Everything hidden inside scroll containers, per axis: sum of scroll- minus client-. */
  hiddenWidth: number;
  hiddenHeight: number;
  /** How far into the expanded page the reporter's viewport starts, per axis. */
  scrolledPastX: number;
  scrolledPastY: number;
  devicePixelRatio: number;
};

/**
 * Read the live page. READ ONLY — nothing here writes to the DOM the reporter
 * is looking at and nothing scrolls it; changing the page is the clone's job
 * (prepareClone), and the clone is thrown away with its iframe.
 *
 * Nested scrollers add up honestly: a child's hidden height is not part of its
 * parent's scrollHeight while the child is still clipped, so expanding both
 * adds both.
 */
export function measureGeometry(doc: Document = document): CaptureGeometry {
  const view = doc.defaultView || window;
  let hiddenWidth = 0;
  let hiddenHeight = 0;
  let scrolledPastX = view.scrollX || 0;
  let scrolledPastY = view.scrollY || 0;

  scrollContainers(doc).forEach(({ el, hiddenX, hiddenY }) => {
    hiddenWidth += hiddenX;
    hiddenHeight += hiddenY;
    // Only an axis that actually scrolls contributes an offset. A box clipped
    // on one axis reports a `scrollLeft`/`scrollTop` of 0 on the other anyway,
    // but reading it as evidence would be a coincidence rather than a rule.
    if (hiddenX) scrolledPastX += el.scrollLeft;
    if (hiddenY) scrolledPastY += el.scrollTop;
  });

  return {
    viewportWidth: view.innerWidth,
    viewportHeight: view.innerHeight,
    documentWidth: doc.documentElement.scrollWidth,
    documentHeight: doc.documentElement.scrollHeight,
    hiddenWidth,
    hiddenHeight,
    scrolledPastX,
    scrolledPastY,
    devicePixelRatio: view.devicePixelRatio || 1,
  };
}

export type CapturePlan = {
  /**
   * Per axis: is the page laid out WHOLE along it (every column / every row), or
   * photographed where the reporter left it? An expanded axis needs the clone
   * opened out (prepareClone); an unexpanded one needs the scroll offset put
   * back, or the capture shows the corner of a box they had scrolled away from.
   */
  expandX: boolean;
  expandY: boolean;
  /**
   * CSS pixels, and `width` is also the width the page is LAID OUT at — see
   * render(). Wider than the viewport only when there are columns to recover.
   */
  width: number;
  height: number;
  scale: number;
  focus: { x: number; y: number };
};

/** The largest scale at which a region still fits the budget. */
function scaleCeiling(width: number, height: number): number {
  return Math.min(
    Math.sqrt(MAX_CAPTURE_PIXELS / (width * height)),
    MAX_CAPTURE_SIDE / width,
    MAX_CAPTURE_SIDE / height
  );
}

/** Where a position on the expanded page falls, as a fraction of it. */
function fractionOf(position: number, size: number): number {
  return Math.min(Math.max(position, 0), size) / size;
}

/**
 * Decide what to photograph. Pure, so the decision can be tested without a
 * rasteriser.
 *
 * Degrade in the order that costs the reporter least:
 *   1. the whole page — every row and every column — at devicePixelRatio;
 *   2. the same at 1x: half the pixels, all of the content;
 *   3. every row, but only the viewport's width of columns, positioned at the
 *      ones the reporter had scrolled to. ROWS BEFORE COLUMNS is deliberate:
 *      the rows are the record, and a page is far likelier to blow the budget
 *      down (a 200-row grid is ~8,000px) than across (a wide table is ~2,600px),
 *      so this rung is what a tall-AND-wide screen actually lands on;
 *   4. only then the viewport, positioned at what they are looking at on both
 *      axes. Even that is strictly better than what this feature did before,
 *      which was the top-left of the page whatever they had scrolled to.
 */
export function planCapture(g: CaptureGeometry): CapturePlan {
  const viewportWidth = Math.max(1, Math.round(g.viewportWidth));
  const viewportHeight = Math.max(1, Math.round(g.viewportHeight));
  const wanted = Math.min(MAX_SCALE, g.devicePixelRatio || 1);

  const fullWidth =
    Math.max(Math.round(g.documentWidth), viewportWidth) + Math.max(0, Math.round(g.hiddenWidth));
  const fullHeight =
    Math.max(Math.round(g.documentHeight), viewportHeight) + Math.max(0, Math.round(g.hiddenHeight));

  const rungs = [
    { expandX: true, expandY: true, width: fullWidth, height: fullHeight },
    { expandX: false, expandY: true, width: viewportWidth, height: fullHeight },
    { expandX: false, expandY: false, width: viewportWidth, height: viewportHeight },
  ];

  // The last rung is the floor, not a candidate: the reporter's own viewport is
  // always small enough, and there is nothing below it to fall to.
  const chosen =
    rungs.find((rung) => Math.min(wanted, scaleCeiling(rung.width, rung.height)) >= MIN_SCALE) ||
    rungs[rungs.length - 1];

  return {
    ...chosen,
    scale: Math.min(wanted, scaleCeiling(chosen.width, chosen.height)),
    // An axis that was NOT expanded is already showing exactly where the
    // reporter was, so there is nothing for the annotation pane to scroll to.
    focus: {
      x: chosen.expandX ? fractionOf(g.scrolledPastX, chosen.width) : 0,
      y: chosen.expandY ? fractionOf(g.scrolledPastY, chosen.height) : 0,
    },
  };
}

/**
 * Lay the clone out at its full height, so the rows hidden inside a scroll
 * container are rendered at all.
 *
 * Heights only — `overflow` is deliberately left alone. Setting `overflow:
 * visible` works too (both were measured), but it also unclips the HORIZONTAL
 * axis, because CSS turns one `visible` axis into `auto` on the other, and a
 * twenty-column grid would then paint over its neighbours. Once a box grows to
 * fit its content there is nothing left to clip. The width is grown by
 * expandHorizontalScrollContainers below, which is the same idea in the other
 * direction and has to state a size rather than say `auto`.
 *
 * `flex` is relaxed only when the parent's main axis is vertical: on a row the
 * same line would collapse the element's WIDTH.
 */
function expandScrollContainers(doc: Document, scrollers: Scroller[]): void {
  const view = doc.defaultView;
  if (!view) return;

  // Vertical scrollers only. A box that is clipped across but not down needs
  // nothing done to its height, and `height: auto` on one that was sized on
  // purpose would move the layout for no gain.
  scrollers
    .filter((s) => s.hiddenY > 0)
    .forEach(({ el: container }) => {
      // Up to <body>: a container cannot outgrow an ancestor that is itself
      // still clamped to the viewport (`h-full`, `max-h-[calc(100vh-320px)]`,
      // `flex-1`).
      for (let el: HTMLElement | null = container; el && el !== doc.body; el = el.parentElement) {
        el.style.setProperty('max-height', 'none', 'important');
        el.style.setProperty('height', 'auto', 'important');
        const parent = el.parentElement;
        const parentStyle = parent ? view.getComputedStyle(parent) : null;
        if (
          parentStyle &&
          (parentStyle.display === 'flex' || parentStyle.display === 'inline-flex') &&
          parentStyle.flexDirection.startsWith('column')
        ) {
          el.style.setProperty('flex', '0 0 auto', 'important');
        }
      }
    });
}

/**
 * The width html2canvas gave this cloned element, in CSS pixels.
 *
 * The inline value FIRST, and that is not a micro-optimisation: on the
 * foreignObject path the cloner has already written every element's computed
 * width into its `style` attribute, so reading it back is reading the number
 * this element will actually be laid out at, and adding to it is stable if a
 * second scroll container's walk comes through the same ancestor. Going via
 * getComputedStyle instead would re-resolve through `box-sizing: border-box`
 * (also baked) and shave the padding off a little more on each pass.
 */
function widthOf(view: Window, el: HTMLElement): number {
  const inline = parseFloat(el.style.width);
  if (Number.isFinite(inline)) return inline;
  const computed = parseFloat(view.getComputedStyle(el).width);
  return Number.isFinite(computed) ? computed : el.getBoundingClientRect().width;
}

/**
 * Lay the clone out at its full width, so the columns hidden inside an
 * `overflow-x-auto` wrapper are rendered at all. The quote generator and the
 * client invoice are both that shape, and this is what the 2026-09-09 report is.
 *
 * It has to STATE a width where the vertical pass can say `height: auto`,
 * because `auto` means opposite things on the two axes: an auto height is the
 * content's height, an auto width is the PARENT's width — which is the width
 * that was too narrow in the first place. So each box grows by exactly what its
 * scroller is hiding, which leaves the wrapper as wide as its own table and
 * every ancestor wide enough to hold it.
 *
 * Ancestors up to and INCLUDING <body>, which is where this differs from the
 * height pass again: <body> is the element html2canvas hands to the renderer,
 * so on the foreignObject path there is nothing above it to inherit from.
 *
 * Two scroll containers sharing an ancestor widen it by both their hidden
 * widths, which is exactly the sum `measureGeometry` reports and `planCapture`
 * sizes the canvas from — so the picture is always wide enough to hold what
 * this produces.
 *
 * `flex` is relaxed only when the parent's main axis is HORIZONTAL — the mirror
 * of the height pass, and for the same reason: a row's `flex: 1 1 0%` ignores
 * the width we just set, and on a column relaxing it would collapse the height.
 */
function expandHorizontalScrollContainers(doc: Document, scrollers: Scroller[]): void {
  const view = doc.defaultView;
  if (!view) return;

  scrollers
    .filter((s) => s.hiddenX > 0)
    .forEach(({ el: container, hiddenX }) => {
      for (let el: HTMLElement | null = container; el; el = el.parentElement) {
        el.style.setProperty('max-width', 'none', 'important');
        el.style.setProperty('width', `${widthOf(view, el) + hiddenX}px`, 'important');
        const parent = el.parentElement;
        const parentStyle = parent ? view.getComputedStyle(parent) : null;
        if (
          parentStyle &&
          (parentStyle.display === 'flex' || parentStyle.display === 'inline-flex') &&
          parentStyle.flexDirection.startsWith('row')
        ) {
          el.style.setProperty('flex', '0 0 auto', 'important');
        }
        if (el === doc.body) break;
      }
    });
}

/**
 * Put the clone back where the reporter had scrolled to, on whichever axes the
 * plan did NOT lay out whole.
 *
 * html2canvas restores scrollTop/scrollLeft on the cloned nodes before onclone
 * runs, but the foreignObject renderer then SERIALISES that clone into an SVG
 * data URL, and a scroll offset is layout state rather than markup: it does not
 * survive the trip. A transform does.
 *
 * Per axis, because the two are decided separately: on the middle rung of the
 * ladder the page is opened out downwards and still photographed at the
 * viewport's width, so the rows must NOT be shifted and the columns must.
 */
function reproduceScrollPositions(doc: Document, plan: CapturePlan, scrollers: Scroller[]): void {
  const view = doc.defaultView;
  scrollers.forEach(({ el }) => {
    const x = plan.expandX ? 0 : el.scrollLeft;
    const y = plan.expandY ? 0 : el.scrollTop;
    if (!x && !y) return;
    Array.from(el.children).forEach((child) =>
      (child as HTMLElement).style?.setProperty('transform', `translate(${-x}px, ${-y}px)`, 'important')
    );
  });
  // The window's own scroll is the same story, and a margin (rather than a
  // transform) leaves `position: fixed` overlays where they belong.
  if (!view) return;
  if (!plan.expandY && view.scrollY) doc.body.style.setProperty('margin-top', `${-view.scrollY}px`, 'important');
  if (!plan.expandX && view.scrollX) doc.body.style.setProperty('margin-left', `${-view.scrollX}px`, 'important');
}

/**
 * Prepare the cloned document html2canvas is about to photograph. The clone,
 * never the live page: the reporter's screen must not move under them.
 *
 * Two of the repairs exist because we render through a foreignObject (see below):
 *
 *  - `<noscript>` is invisible in a live browser but is ordinary text inside a
 *    foreignObject, so index.html's "You need to enable JavaScript to run this
 *    app." printed across the top of every screenshot and pushed the whole page
 *    down by ~48px.
 *  - the foreignObject is rasterised from a data: URL, which cannot pull in
 *    external sub-resources, so an <img> that only carries a URL comes out
 *    blank. That is what emptied the TIDAL logo out of the header.
 */
async function prepareClone(doc: Document, plan: CapturePlan, useForeignObject: boolean): Promise<void> {
  // ONE sweep of the clone, shared by all three passes below. The sweep is a
  // getComputedStyle on every element in the document — thousands of them on
  // the 200-row grid this feature exists for — and the capture is already
  // racing an 8-second clock it can lose (see CAPTURE_TIMEOUT_MS), so the
  // second axis is not a reason to walk the document twice.
  //
  // The hidden amounts are therefore all measured BEFORE anything is expanded,
  // which is also what we want: growing a box downwards can take a scrollbar
  // away and change what it hides across, and each pass should act on the page
  // the reporter actually had.
  const scrollers = scrollContainers(doc);
  if (plan.expandY) expandScrollContainers(doc, scrollers);
  if (plan.expandX) expandHorizontalScrollContainers(doc, scrollers);
  if (!plan.expandX || !plan.expandY) reproduceScrollPositions(doc, plan, scrollers);

  if (!useForeignObject) return;

  doc.querySelectorAll('noscript').forEach((node) => node.remove());

  // What a person picked, ticked or typed lives in PROPERTIES, and the
  // serialised clone keeps only attributes, so every <select> came out at its
  // first option: the Edit Item row "1 [—] = 3 [—]", Currency "—" and Source
  // "Manual" of USC-BUG-0028 (MC-7), under a summary that correctly read
  // "1 CASE = 3 PCS". Written back as markup. A password is never written into
  // markup, so its box photographs empty.
  doc.querySelectorAll('select').forEach((select) => {
    const chosen = select.selectedIndex;
    Array.from(select.options).forEach((option, i) => option.toggleAttribute('selected', i === chosen));
  });
  doc.querySelectorAll('textarea').forEach((area) => {
    area.textContent = area.value;
  });
  doc.querySelectorAll('input').forEach((input) => {
    if (input.type === 'checkbox' || input.type === 'radio') input.toggleAttribute('checked', input.checked);
    else if (input.type !== 'password' && input.type !== 'file') input.setAttribute('value', input.value);
  });

  // Keyed by URL so a repeated asset (an avatar in every row) is fetched once.
  const inlined = new Map<string, Promise<string>>();

  const toDataUrl = (src: string): Promise<string> => {
    const cached = inlined.get(src);
    if (cached) return cached;
    const pending = fetch(src, { credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then(
        (blob) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result));
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
          })
      );
    inlined.set(src, pending);
    return pending;
  };

  await Promise.all(
    Array.from(doc.images).map(async (img) => {
      const src = img.currentSrc || img.src;
      if (!src || src.startsWith('data:')) return;
      try {
        img.src = await toDataUrl(src);
      } catch (err) {
        // A cross-origin asset served without a CORS header (GCS) cannot be
        // inlined. It renders blank — the documented limit of DOM capture —
        // and losing one image must never cost us the bug report.
        // eslint-disable-next-line no-console
        console.warn('[bugReporter] could not inline an image for capture:', src, err);
      }
    })
  );
}

async function render(plan: CapturePlan, useForeignObject: boolean): Promise<HTMLCanvasElement> {
  // Dynamic import, deliberately. html2canvas is ~200KB and this is a
  // temporary feature; keeping it out of the main chunk means the bundle
  // (already carrying exceljs/xlsx/pdf-parse — TECH_DEBT #19) is unaffected,
  // and removing the dependency later cannot destabilise the main bundle.
  const html2canvas = (await import('html2canvas')).default;

  return html2canvas(document.body, {
    // ALWAYS the origin, never the scroll offset. html2canvas's foreignObject
    // renderer applies x/y twice — once as a ctx.translate, again as the
    // drawImage offset — against an SVG that only ever holds the top
    // width × height of the document, so passing window.scrollY brought a
    // window-scrolled page back BLANK (measured). What gets photographed is
    // decided by how the clone is laid out (prepareClone), not by asking the
    // library to pan.
    x: 0,
    y: 0,
    // The REGION, and nothing more. Asking for a width past the viewport does
    // not widen the page inside it: on the foreignObject path the cloner bakes
    // every element's computed width inline (`copyStyles`), so the layout is
    // frozen at whatever the reporter's screen made it and the extra pixels
    // come back as white space — measured in Chromium, a 2,083px-wide capture
    // of a 1,000px viewport had its last painted pixel at x=992. Widening the
    // CLONE is what fills them (prepareClone), exactly as it is for height.
    // `windowWidth` is deliberately not passed for the same reason: it resizes
    // the throwaway clone iframe, which the baked widths then ignore.
    width: plan.width,
    height: plan.height,
    // The same renderer sizes its SVG in CSS pixels (width * scale), so a
    // scale > 1 magnifies rather than super-samples: at scale 2 the capture
    // keeps the top-left quarter of the page, blown up (measured, on this
    // app's own structure). Hence 1 here. The fallback renderer paints
    // properly and keeps the planned scale.
    scale: useForeignObject ? 1 : plan.scale,
    // GCS-hosted images are cross-origin; without this they render blank.
    // With it they still may, if the bucket sends no CORS header — which is
    // acceptable, and is why nothing here treats an image as fatal.
    useCORS: true,
    // A tainted canvas cannot be read back with toBlob(), which would turn a
    // single third-party image into a total capture failure.
    allowTaint: false,
    backgroundColor: '#ffffff',
    logging: false,
    foreignObjectRendering: useForeignObject,
    onclone: (doc: Document) => prepareClone(doc, plan, useForeignObject),
  });
}

/**
 * Capture what the reporter is complaining about, wherever they have scrolled to.
 *
 * THE WHOLE SCROLLABLE PAGE, not the viewport, and the viewport is the bug this
 * replaced: the capture used to be `x: window.scrollX, y: window.scrollY,
 * width/height: the window`. On the screen this feature exists for — the
 * vendor-invoice review, a fullScreen <Modal> whose body is `overflow-y-auto` —
 * the grid scrolls INSIDE that container while `window.scrollY` stays 0, so row
 * 140 of a 200-row invoice was photographed as row 1 and a bug you had to
 * scroll to see could not be reported with a picture of itself.
 *
 * BOTH AXES, since 2026-09-09. The first fix took the page's height and left
 * its width at the viewport's, which on a wide table is the same defect turned
 * sideways: the quotation grid and the client invoice put their columns inside
 * an `overflow-x-auto` wrapper, nothing was measuring what that wrapper hid, and
 * the clone was neither opened out across nor put back where the reporter had
 * scrolled it — so a capture of a margin bug in the right-hand columns was a
 * picture of the LEFT-hand ones. Reported by the client, 2026-09-09.
 *
 * Expanding the container in the CLONE is what makes those rows and columns
 * exist to be photographed, on both axes and for the same reason: an internally
 * scrolled box is clipped by its own overflow, so asking html2canvas for a
 * bigger region on its own changes nothing but how much white space comes back.
 *
 * planCapture bounds it. Past 16MP the scale degrades first and the region only
 * afterwards, back to the viewport the reporter is looking at.
 *
 * RENDERED THROUGH A foreignObject, WHICH IS NOT THE LIBRARY DEFAULT, and the
 * reason is measured rather than stylistic. html2canvas's own text renderer
 * finds a font's baseline by probing `img.offsetTop - span.offsetTop + 2`,
 * which mixes two different vertical metrics. For this app's font (Montserrat,
 * whose inline-box ascent is ~1.33em while its fontBoundingBoxAscent is 1.0em)
 * the probe overshoots by 7px at 12px text and 12px at 24px, so EVERY label was
 * painted below its own box and clipped — sidebar, table headers, the lot. It
 * is a bug in an unmaintained library, so we do not use that code path:
 * foreignObject hands the job to the browser's own renderer, which is correct
 * by definition. prepareClone above pays the costs that come with it.
 *
 * Falls back to the default renderer if foreignObject fails outright (Chrome
 * refuses very large SVGs, and Safari has its own bugs here). That output has
 * the baseline flaw described above, but a shifted screenshot beats none.
 *
 * NEVER THROWS. A failed capture returns `{ capture: null, error }` and the
 * caller carries on with an image-less report — losing the bug report because
 * the screenshot library tripped over a CSS filter would be the worse outcome.
 */
export async function captureScreen(): Promise<CaptureResult> {
  try {
    const geometry = measureGeometry();
    const plan = planCapture(geometry);

    let canvas: HTMLCanvasElement;
    try {
      canvas = await Promise.race([render(plan, true), timeout(CAPTURE_TIMEOUT_MS)]);
    } catch (foreignObjectErr) {
      // Only an outright failure is worth a second attempt. Running out of
      // clock is not: the default renderer walks and paints every node itself,
      // so it does strictly more work and will not beat a deadline the
      // browser's own rasteriser just missed. Retrying on a timeout would only
      // make the reporter wait 8 more seconds for the same answer.
      if (foreignObjectErr instanceof CaptureTimeout) throw foreignObjectErr;

      // Reported, not swallowed — if this starts happening we want it visible.
      // eslint-disable-next-line no-console
      console.warn(
        '[bugReporter] foreignObject capture failed, falling back to the default renderer ' +
          '(text will sit low — see the note on captureScreen):',
        foreignObjectErr
      );
      canvas = await Promise.race([render(plan, false), timeout(CAPTURE_TIMEOUT_MS)]);
    }

    return {
      capture: {
        canvas,
        width: canvas.width,
        height: canvas.height,
        focus: plan.focus,
        viewportsWide: plan.width / Math.max(1, Math.round(geometry.viewportWidth)),
      },
      error: null,
    };
  } catch (err: any) {
    // Reported, not swallowed: the modal tells the reporter the screenshot
    // failed so they can describe the problem in words instead.
    // eslint-disable-next-line no-console
    console.warn('[bugReporter] screen capture failed:', err);
    return { capture: null, error: err?.message || 'The screen could not be captured.' };
  }
}

export function useScreenshot() {
  const [capturing, setCapturing] = useState(false);

  const capture = useCallback(async (): Promise<CaptureResult> => {
    setCapturing(true);
    try {
      return await captureScreen();
    } finally {
      setCapturing(false);
    }
  }, []);

  return { capture, capturing };
}
